import { BaseRate, RateCardLoading } from "models/ratecard"
import {
  ChangedPlanRecord,
  PlanRecordType,
} from "routes/Base/Plans/Details/types"
import {
  MPLScenario,
  MPLScenarioFilterContainer,
  MediaPlan,
  MediaPlanAggregations,
  MediaPlanSum,
  MediaPlanVersion,
  ScenarioAsMediaPlan,
} from "models/mediaPlan"
import {
  SpotMatching,
  SpotMatchingFilters,
  SpotMatchingFiltersResponse,
  SpotsSummaryFilterOptions,
  SpotsSummaryResponse,
  SpotsTotalSummaryResponse,
  SummarizedSpot,
} from "models/spots"
import { Supplier, SupplierTuple } from "models/supplier"
import { http, httpExport, httpNoTimeout } from "./http"

import { ApproversResponse } from "./campaigns"
import { ClientAgencyTarget } from "models/targets"
import { ColumnDefinition } from "components/Bable"
import { CopyResource } from "models/copies"
import { CreateMediaPlanPayload } from "models/mediaPlan/create"
import { DocId } from "models/common"
import { Paginated } from "api/types"
import { decodeBase64ToBlob } from "utils/files"

export const fetchPlans = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res = await http("media-plans", { searchParams }).json()
  return res as Paginated<MediaPlan>
}

export const fetchPlansByCampaign = async (
  _key: string,
  campaignId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  searchParams.set("campaignId", campaignId)
  const res = await http("media-plans", { searchParams }).json()
  return res as Paginated<MediaPlan>
}

export const fetchPlansAggregations = async (
  _key: string,
  campaignId?: string,
) => {
  const res = await http(
    `media-plans/aggregations${campaignId ? `?campaignId=${campaignId}` : ""}`,
  ).json()
  return res as MediaPlanAggregations
}

export const fetchPlan = async (
  _key: string,
  id: string,
  version: string | null,
) => {
  const searchParams = new URLSearchParams()
  !!version && searchParams.set("version", version)
  const res = await httpNoTimeout(`media-plans/${id}`, { searchParams }).json()
  return res as MediaPlan
}

export const fetchPlanVersions = async (_key: string, id: string) => {
  const { data } = await http(`media-plans/${id}/versions`).json<{
    data: Array<MediaPlanVersion>
  }>()
  return data
}

export const fetchPlanSummary = async (
  _key: string,
  id: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${id}/financial-sum`, {
    searchParams,
  }).json()
  return res as MediaPlanSum
}

export const patchPlanVersionComment = async ({
  id,
  comment,
}: {
  id: string
  comment: string
}) => {
  const res = await http
    .patch(`media-plans/audit/${id}/comment`, { json: { comment } })
    .json()
  return res as MediaPlanVersion
}

export const setPlanVersionAsLatest = async (obj: {
  id: string
  version: string
}) => {
  const { id, version } = obj
  const searchParams = new URLSearchParams({ version })

  return http.post(`media-plans/${id}/change-version`, { searchParams })
}

type UploadMediaPlanPld = {
  file: File
  campaignId: string
  mediaPlanName: string
  clientId: string
  planId?: string
  ignoreSupplierValidation?: boolean
}

type UploadMediaPlanResponse = {
  mediaPlanId: string
}

export const uploadMediaPlan = async (pld: UploadMediaPlanPld) => {
  const {
    file,
    campaignId,
    clientId,
    mediaPlanName,
    planId,
    ignoreSupplierValidation,
  } = pld

  const formData = new FormData()
  formData.append("file", file)
  formData.append("campaignId", campaignId)
  formData.append("clientId", clientId)
  formData.append("mediaPlanName", mediaPlanName)
  formData.append(
    "ignoreSupplierValidation",
    String(Boolean(ignoreSupplierValidation)),
  )

  const url = planId ? `media-plans/${planId}/re-import` : "media-plans/import"

  const res = await httpNoTimeout.post(url, { body: formData }).json()

  console.log("uploadMediaPlan::res::", res)

  return res as UploadMediaPlanResponse
}

export const createMediaPlan = async (pld: CreateMediaPlanPayload) => {
  const res = await http.post("media-plans", { json: pld }).json()
  return res as DocId
}

export const updateFlowPlan = async ({
  id,
  payload,
}: {
  id: string
  payload?: Partial<MediaPlan>
}) => {
  const { name, start, end, budget, flowPlanItems, exchangeRate } =
    payload || {}
  const json = {
    name,
    start,
    end,
    budget,
    flowPlanItems,
    currencyExchangeRate: exchangeRate,
  }
  const res = await httpNoTimeout.patch(`media-plans/${id}`, { json }).json()
  return res
}

export const downloadMediaPlanTemplate = async (
  id: string,
  filename: string,
  agencyId?: string,
) => {
  const searchParams = new URLSearchParams({ clientId: id })
  !!agencyId && searchParams.set("agencyId", agencyId)
  const blob = await httpNoTimeout("media-plans/template", {
    searchParams,
  }).blob()
  const objectUrl = URL.createObjectURL(blob)
  var fileLink = document.createElement("a")
  fileLink.href = objectUrl
  fileLink.download = filename
  fileLink.click()
}

export type MediaPlanLinesExport = { id: string; type: PlanRecordType }

type MediaPlanLinesExportPayload = {
  clientId: string
  atbCalendarView?: string
  data?: Array<MediaPlanLinesExport>
}

// type MediaPlanSpotsExportPayload = {
//   mediaPlanId: string
//   spots?: Array<SummarizedSpot>
//   filteredTvMediaFields: any
//   flowPlanItemsWeNeed: any
// }

export const exportMediaPlanLines = async (
  planId: string,
  pld: MediaPlanLinesExportPayload,
) => {
  return http
    .post(`media-plans/${planId}/export`, { json: pld, timeout: false })
    .blob()
}

export const exportAtbMediaPlan = async (
  planId: string,
  pld: MediaPlanLinesExportPayload,
) => {
  if (!!process.env.REACT_APP_LEGACY_MBA) {
    return http
      .post(`media-plans/${planId}/export-atb`, { json: pld, timeout: false })
      .blob()
  }
  return httpExport
    .get(
      `${planId}/mba${
        pld.atbCalendarView ? `?atbCalendarView=${pld.atbCalendarView}` : ""
      }`,
      {
        timeout: false,
      },
    )
    .blob()
}
export const exportMediaPlanSpots = async (
  mediaPlanId: string,
  columns: ColumnDefinition[],
  filters: string,
) => {
  const urlParams = new URLSearchParams(filters)

  const searchParams = urlParams.toString().replace(/(?<=sort=[^&]*)%2C/gi, ",")

  return http(`media-plans/${mediaPlanId}/spots/export-spots?${searchParams}`)
    .text()
    .then((encoded) => decodeBase64ToBlob(encoded))
}

export const exportDigitalMediaPlan = async (planId: string) => {
  return httpExport.get(`${planId}/dt`, { timeout: false }).blob()
}

export const submitMediaPlan = async (id: string) => {
  const res = await http.post(`media-plans/${id}/submit`).json()
  return res as MediaPlan
}

export const validateMediaPlan = async (id: string) => {
  const res = await http.post(`media-plans/${id}/validate`).json()
  return res as MediaPlan
}

type ApproveMediaPlanPld = {
  file?: File
  id: string
  clientApprover?: string
  approvalDate?: string
  comment?: string
}

export const approveMediaPlan = async (pld: ApproveMediaPlanPld) => {
  const { file, id, clientApprover, comment, approvalDate } = pld
  const formData = new FormData()

  if (file !== undefined) {
    formData.append("approvalDocument", file)
  }
  if (clientApprover !== undefined) {
    formData.append("clientApprover", clientApprover)
  }
  if (comment !== undefined) {
    formData.append("comment", comment)
  }
  if (approvalDate !== undefined) {
    formData.append("approvalDate", approvalDate)
  }

  const res = await httpNoTimeout
    .post(`media-plans/${id}/approve`, { body: formData })
    .json()
  return res
}

export const commentMediaPlan = async (params: {
  id: string
  comment: string
}) => {
  const res = await http
    .post(`media-plans/${params.id}/comment`, {
      json: { comment: params.comment },
    })
    .json()
  return res
}

export const deleteMediaPlan = async (id: string) =>
  await http.delete(`media-plans/${id}`)

export const copyMediaPlan = async (obj: {
  id: string
  version?: string | null
}) => {
  const { id, version = undefined } = obj
  const versionParam = version ? `?version=${version}` : ""
  return http
    .post(`media-plans/${id}/copy${versionParam}`)
    .json<{ id: string }>()
}

export const completeMediaPlan = async (id: string) =>
  await http.patch(`media-plans/${id}/complete`)

export const fetchMediaPlanApprovers = async (
  _key: string,
  id: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${id}/approvers`, { searchParams }).json()
  return res as ApproversResponse
}

export const fetchBaseRates = async (
  _key: string,
  id: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${id}/rate-cards`, {
    searchParams,
  }).json()

  return res as { baseRate: BaseRate; loadingResources?: RateCardLoading[] }
}

type PackageOption = {
  id: string
  name: string
  mediaOwner: Supplier
  intermediary?: Supplier
}

export const fetchPackages = async (
  _key: string,
  id: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${id}/packages`, { searchParams }).json()
  return res as Paginated<PackageOption>
}

export const getPackageFlowPlanItems = async (
  _key: string,
  id: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`packages/${id}/flow-plan-items`, {
    searchParams,
  }).json()
  return res as Paginated<ChangedPlanRecord>
}

export type FeeOption = {
  id: string
  label: string
  amountType: "PERCENTAGE" | "FIXED_AMOUNT"
  amount: number
  computedOn: "GROSS" | "NET" | "PRODUCTION"
  semantic:
    | "MANAGEMENT"
    | "BOOKING"
    | "PRODUCTION"
    | "TALENT"
    | "RETAINER"
    | "PLANNING"
    | "OTHERS"
  type: string
}

export const fetchMediaPlanFees = async (
  _key: string,
  id: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${id}/fees`, { searchParams }).json()
  return res as Paginated<FeeOption>
}

export const acknowledgeMediaPlan = async (id: string) => {
  return await http.post(`media-plans/${id}/acknowledge`)
}

export const acknowledgeMediaPlanLineItem = async ({
  planId,
  id,
  type,
}: {
  planId: string
  id: string
  type: string
}) => {
  const searchParams = new URLSearchParams({ type })
  const res = await http.post(
    `media-plans/${planId}/flow-plan-items/${id}/acknowledge`,
    {
      searchParams,
    },
  )

  return res
}

type SplitFrames = {
  timeFrames: Array<{ start: string; end: string }>
}

export const splitMediaPlanLineItem = async ({
  id,
  liId,
  pld,
}: {
  id: string
  liId: string
  pld: SplitFrames
}) => {
  return await http.post(`media-plans/${id}/line-items/${liId}/split`, {
    json: { ...pld },
  })
}

export const getMediaPlanSplitOptions = async (
  _key: string,
  id: string,
  liId: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${id}/line-items/${liId}/splitFrames`, {
    searchParams,
  }).json()
  return res as SplitFrames
}

export const fetchPlanTargets = async (_key: string, id: string) => {
  const { data } = await http(`media-plans/${id}/available-targets`).json<{
    data: Partial<ClientAgencyTarget>[]
  }>()
  return data
}

export const fetchPlanCopies = async (
  _key: string,
  id: string,
  params: string,
) => {
  const searchParams = new URLSearchParams(params)
  const { data } = await http(`media-plans/${id}/available-copies`, {
    searchParams,
  }).json<{ data: CopyResource[] }>()
  return data
}

export const changeMediaPlanLinesVendor = async (payload: {
  planId: string
  vendor: SupplierTuple
  lineItemIds?: string[]
  packageIds?: string[]
}) => {
  const { planId, ...json } = payload
  return await http.post(`media-plans/${planId}/change-vendor`, { json })
}

export const fetchSpotsSummary = async (
  _key: string,
  id: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
    .toString()
    .replace(/(?<=sort=[^&]*)%2C/gi, ",")
  const res = await http(`media-plans/${id}/spots?${searchParams}`).json()
  return res as SpotsSummaryResponse
}

export const fetchSpotsTotalSummary = async (
  _key: string,
  id: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
    .toString()
    .replace(/(?<=sort=[^&]*)%2C/gi, ",")
  const res = await http(
    `media-plans/${id}/spots/total-summary?${searchParams}`,
  ).json()
  return res as SpotsTotalSummaryResponse
}

export const fetchSpotsSummaryFilterOptions = async (
  _key: string,
  planId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${planId}/spots/filter-options`, {
    searchParams,
  }).json()

  return res as SpotsSummaryFilterOptions
}

export const matchPlanSpots = async (id: string) => {
  return await httpNoTimeout
    .post(`media-plans/${id}/spots/auto-match-post-evaluate`)
    .json()
}

export const postEvaluateSpots = async (id: string) => {
  return await httpNoTimeout
    .post(`media-plans/${id}/spots/post-evaluate?match=false`)
    .json()
}

export const autoAutomaticMatchPlanSpots = async (payload: {
  id: string
  tolerance: number
}) => {
  return await httpNoTimeout
    .post(
      `media-plans/${payload.id}/spots/auto-match?tolerance=${payload.tolerance}`,
    )
    .json()
}

export const unmatchSpots = async (payload: {
  planId: string
  spotIds: string[]
}) => {
  const { planId, spotIds } = payload
  if (!planId || !spotIds) return Promise.reject()

  return await http.post(`media-plans/${planId}/spots/unmatch`, {
    json: spotIds,
  })
}

export const patchSummarizedSpot = async (
  payload: { planId?: string } & Partial<SummarizedSpot>,
) => {
  const { planId, id, subMediaType, bookingOrderNumber, ...json } = payload

  if (!planId || !id) return Promise.reject()

  const res: SummarizedSpot = await http
    .patch(`media-plans/${planId}/spots/${id}`, { json })
    .json()
  return {
    ...res,
    subMediaType: subMediaType,
    bookingOrderNumber,
  }
}

export const markOfficialPlan = async (pld: { id: string }) => {
  const { id } = pld
  const res = await http.post(`media-plans/${id}/mark-official`)
  return res
}

export const fetchSpotMatchings = async (
  _key: string,
  planId: string,
  spotId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http(`media-plans/${planId}/spots/${spotId}/matching`, {
    searchParams,
  }).json()

  return res as Paginated<SpotMatching>
}

export const fetchSpotMatchingFilters = async (
  _key: string,
  planId: string,
  spotId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = (await http(
    `media-plans/${planId}/spots/${spotId}/matching/filters`,
    {
      searchParams,
    },
  ).json()) as SpotMatchingFiltersResponse

  const expectedRes = {
    products: res.productTuple?.map((it) => ({
      id: it.productId,
      name: it.productName,
    })),
    copies: res.copyTuple?.map((it) => ({
      id: it.copyId,
      name: it.copyName,
    })),
    channels: res.channelTuple?.map((it) => ({
      id: it.channelId,
      name: `${it.localName} (${it.channelName})`,
    })),
    programs: res.programTuple?.map((it) => ({
      id: it.programId,
      name: it.programName,
    })),
  }

  return expectedRes as SpotMatchingFilters
}

export const manuallyMatchSpot = async (payload: {
  planId: string
  spotId: string
  externalId: string
}) => {
  const { planId, spotId, externalId } = payload
  const res = await http
    .post(
      `media-plans/${planId}/spots/${spotId}/semi-automatic-match/${externalId}`,
    )
    .json()

  return res as SummarizedSpot
}

export const fetchCanManualMatch = async ({
  mediaPlanId,
  spotId,
}: {
  mediaPlanId: string
  spotId: string
}) => {
  const res = await http
    .get(`media-plans/${mediaPlanId}/spots/copy/info/${spotId}`)
    .json()
  return res as { canManualMatch: boolean; copyExternalId?: string }
}

export const fetchMediaPlanScenario = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get("media-plan-scenario", { searchParams }).json()
  return res as Paginated<MPLScenario>
}

export const fetchScenarioAsMediaPlan = async (
  _key: string,
  id: string,
  params?: Record<string, string>,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get(`media-plan-scenario/${id}/media-plan`, { searchParams })
    .json()
  return res as ScenarioAsMediaPlan
}

export const fetchScenarioFilters = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get("media-plan-scenario/filter-options", { searchParams })
    .json()
  return res as MPLScenarioFilterContainer
}
